import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MagnifyingGlassIcon,
  TrophyIcon,
  ClockIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import Card from '../components/UI/Card';
import spinService, {
  SpinWinStatus,
  SpinPrizeType,
} from '../api/services/spin.service';

/* =========================================================
   HELPERS
========================================================= */
const fmtAmount = (n: number | undefined | null): string => {
  if (n == null || !Number.isFinite(n)) return '0';
  const rounded = Math.round((n + Number.EPSILON) * 1000) / 1000;
  return rounded.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  });
};

const typeLabel = (t: SpinPrizeType): string => {
  if (t === 'money') return 'Money';
  if (t === 'item') return 'Item';
  if (t === 'spin_again') return 'Spin Again';
  return 'Nothing';
};

const statusBadge = (s: SpinWinStatus) => {
  const map: Record<
    SpinWinStatus,
    { bg: string; text: string; label: string }
  > = {
    completed: {
      bg: 'bg-green-50',
      text: 'text-green-700',
      label: 'Completed',
    },
    pending: {
      bg: 'bg-amber-50',
      text: 'text-amber-700',
      label: 'Pending',
    },
    rejected: {
      bg: 'bg-red-50',
      text: 'text-red-700',
      label: 'Rejected',
    },
  };
  const m = map[s];
  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${m.bg} ${m.text}`}
    >
      {m.label}
    </span>
  );
};

const StatBox: React.FC<{
  label: string;
  value: string | number;
  icon: React.ReactNode;
  color?: string;
}> = ({ label, value, icon, color = 'text-primary-500' }) => (
  <div className="border border-gray-100 rounded-lg p-3">
    <div className="flex items-center gap-2 mb-1">
      <span className={color}>{icon}</span>
      <p className="text-[10px] text-gray-500 uppercase tracking-wide">
        {label}
      </p>
    </div>
    <p className="text-base font-bold text-gray-900">{value}</p>
  </div>
);

/* =========================================================
   PAGE
========================================================= */
const SpinWins: React.FC = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [localSearch, setLocalSearch] = useState('');
  const [prizeType, setPrizeType] = useState<'all' | SpinPrizeType>('all');
  const [status, setStatus] = useState<'all' | SpinWinStatus>('all');
  const [minDate, setMinDate] = useState('');
  const [maxDate, setMaxDate] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  /* Stats */
  const { data: stats } = useQuery({
    queryKey: ['spin-win-stats'],
    queryFn: () => spinService.getWinStats(),
    staleTime: 60 * 1000,
  });

  /* List */
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      'spin-wins',
      page,
      limit,
      searchTerm,
      prizeType,
      status,
      minDate,
      maxDate,
    ],
    queryFn: () =>
      spinService.getWins({
        page,
        limit,
        search: searchTerm || undefined,
        prizeType,
        status,
        minDate: minDate || undefined,
        maxDate: maxDate || undefined,
      }),
    staleTime: 60 * 1000,
  });

  const wins = data?.wins || [];
  const pagination = data?.pagination || {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  };

  const handleSearchSubmit = () => {
    setSearchTerm(localSearch);
    setPage(1);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearchSubmit();
  };

  const handleClearFilters = () => {
    setLocalSearch('');
    setSearchTerm('');
    setPrizeType('all');
    setStatus('all');
    setMinDate('');
    setMaxDate('');
    setPage(1);
  };

  const handleStatusChange = async (
    id: string,
    next: SpinWinStatus
  ) => {
    setBusyId(id);
    try {
      await spinService.updateWinStatus(id, next);
      queryClient.invalidateQueries({ queryKey: ['spin-wins'] });
      queryClient.invalidateQueries({ queryKey: ['spin-win-stats'] });
    } finally {
      setBusyId(null);
    }
  };

  const hasFilters =
    !!searchTerm ||
    prizeType !== 'all' ||
    status !== 'all' ||
    !!minDate ||
    !!maxDate;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Spin Wins</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Every spin outcome, money credited, and pending item to fulfil.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <StatBox
          label="Total Wins"
          value={(stats?.totalWins ?? 0).toLocaleString()}
          icon={<TrophyIcon className="w-4 h-4" />}
        />
        <StatBox
          label="Today"
          value={(stats?.todayWins ?? 0).toLocaleString()}
          icon={<ClockIcon className="w-4 h-4" />}
          color="text-blue-500"
        />
        <StatBox
          label="Money Paid"
          value={`ETB ${fmtAmount(stats?.totalMoneyDistributed)}`}
          icon={<CurrencyDollarIcon className="w-4 h-4" />}
          color="text-green-500"
        />
        <StatBox
          label="Pending"
          value={(stats?.pendingCount ?? 0).toLocaleString()}
          icon={<ClockIcon className="w-4 h-4" />}
          color="text-amber-500"
        />
        <StatBox
          label="Completed"
          value={(stats?.completedCount ?? 0).toLocaleString()}
          icon={<CheckCircleIcon className="w-4 h-4" />}
          color="text-green-500"
        />
        <StatBox
          label="Rejected"
          value={(stats?.rejectedCount ?? 0).toLocaleString()}
          icon={<XCircleIcon className="w-4 h-4" />}
          color="text-red-500"
        />
      </div>

      {/* Filters */}
      <Card padding="sm" className="mb-4">
        <div className="flex flex-col md:flex-row md:items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[180px]">
            <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search by telegram id or label..."
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="w-full text-sm border border-gray-200 rounded-lg pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>

          <select
            value={prizeType}
            onChange={(e) => {
              setPrizeType(e.target.value as any);
              setPage(1);
            }}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
          >
            <option value="all">All Types</option>
            <option value="money">Money</option>
            <option value="item">Item</option>
          </select>

          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as any);
              setPage(1);
            }}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
          >
            <option value="all">All Statuses</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option>
          </select>

          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500 whitespace-nowrap">
              From
            </label>
            <input
              type="date"
              value={minDate}
              onChange={(e) => {
                setMinDate(e.target.value);
                setPage(1);
              }}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>

          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500 whitespace-nowrap">
              To
            </label>
            <input
              type="date"
              value={maxDate}
              onChange={(e) => {
                setMaxDate(e.target.value);
                setPage(1);
              }}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>

          <button
            onClick={handleSearchSubmit}
            className="text-sm px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
          >
            Search
          </button>

          {hasFilters && (
            <button
              onClick={handleClearFilters}
              className="text-sm px-4 py-2 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Clear
            </button>
          )}
        </div>
      </Card>

      {/* Table */}
      <Card padding="sm">
        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-solid border-primary-500 border-t-transparent" />
          </div>
        ) : error ? (
          <div className="text-center py-6">
            <p className="text-sm text-red-500">
              {error instanceof Error
                ? error.message
                : 'Failed to load spin wins'}
            </p>
            <button
              onClick={() => refetch()}
              className="mt-3 px-4 py-2 bg-primary-500 text-white rounded-lg text-sm hover:bg-primary-600"
            >
              Retry
            </button>
          </div>
        ) : wins.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-sm text-gray-500">No spin wins found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-[10px] text-gray-500 uppercase tracking-wide border-b border-gray-100">
                  <th className="px-3 py-2 font-medium">User</th>
                  <th className="px-3 py-2 font-medium">Prize</th>
                  <th className="px-3 py-2 font-medium">Type</th>
                  <th className="px-3 py-2 font-medium">Amount</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Date</th>
                  <th className="px-3 py-2 font-medium text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {wins.map((w) => (
                  <tr
                    key={w.id}
                    className="border-b border-gray-50 hover:bg-gray-50/60"
                  >
                    <td className="px-3 py-3">
                      <p className="text-xs font-medium text-gray-900">
                        {w.userName}
                      </p>
                      <p className="text-[10px] text-gray-400">
                        @{w.telegramId}
                      </p>
                    </td>
                    <td className="px-3 py-3">
                      <p className="text-xs text-gray-800">
                        {w.prizeLabel}
                      </p>
                      {w.prizeLabelAlt && (
                        <p className="text-[10px] text-gray-400">
                          {w.prizeLabelAlt}
                        </p>
                      )}
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-600">
                      {typeLabel(w.prizeType)}
                    </td>
                    <td className="px-3 py-3 text-xs font-medium text-green-600">
                      {w.prizeType === 'money'
                        ? `ETB ${fmtAmount(w.amount)}`
                        : '—'}
                    </td>
                    <td className="px-3 py-3">{statusBadge(w.status)}</td>
                    <td className="px-3 py-3 text-xs text-gray-600 whitespace-nowrap">
                      {new Date(w.createdAt).toLocaleString()}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        {w.status === 'pending' && (
                          <>
                            <button
                              onClick={() =>
                                handleStatusChange(w.id, 'completed')
                              }
                              disabled={busyId === w.id}
                              className="px-2.5 py-1 rounded-md text-[10px] font-medium bg-green-50 text-green-700 hover:bg-green-100 disabled:opacity-50"
                            >
                              {busyId === w.id ? '...' : 'Complete'}
                            </button>
                            <button
                              onClick={() =>
                                handleStatusChange(w.id, 'rejected')
                              }
                              disabled={busyId === w.id}
                              className="px-2.5 py-1 rounded-md text-[10px] font-medium bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50"
                            >
                              Reject
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.total > 0 && (
          <div className="flex items-center justify-between px-3 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              Page {pagination.page} of {pagination.pages} ·{' '}
              {pagination.total} total
            </p>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-2.5 py-1 text-xs rounded-md border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >
                Prev
              </button>
              <button
                onClick={() =>
                  setPage((p) => Math.min(pagination.pages, p + 1))
                }
                disabled={page >= pagination.pages}
                className="px-2.5 py-1 text-xs rounded-md border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default SpinWins;